import React, { useState, useEffect } from 'react';
import { serviceAPI, taskTypeAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { BookOpen, Plus, Edit2, Trash2, Save, X, ChevronDown, ChevronRight, Image, FileText } from 'lucide-react';
import api from '../api/api';

const Workflows = () => {
  const { isPartner, isManager, isStaff } = useAuth();
  const canEdit = isPartner || isManager;
  
  const [services, setServices] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // UI State
  const [expandedTaskType, setExpandedTaskType] = useState(null);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [showAddForm, setShowAddForm] = useState(null);
  
  // Form state
  const [stepForm, setStepForm] = useState({
    title: '',
    description: '',
    screenshot_url: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [servicesRes, taskTypesRes, workflowsRes] = await Promise.all([
        serviceAPI.getAll(),
        taskTypeAPI.getAll(),
        api.get('/workflows'),
      ]);
      setServices(servicesRes.data);
      setTaskTypes(taskTypesRes.data);
      setWorkflows(workflowsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // If workflows endpoint doesn't exist yet, just set empty
      if (error.response?.status === 404) {
        setWorkflows([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const getWorkflowForTaskType = (taskTypeId) => {
    return workflows.find(w => w.task_type_id === taskTypeId);
  };

  const handleAddStep = async (taskTypeId) => {
    if (!stepForm.title.trim()) {
      alert('Please enter step title');
      return;
    }

    try {
      const workflow = getWorkflowForTaskType(taskTypeId);
      const newStep = {
        id: Date.now().toString(),
        order: workflow ? workflow.steps.length + 1 : 1,
        ...stepForm,
      };

      if (workflow) {
        // Update existing workflow
        await api.put(`/workflows/${workflow.id}`, {
          steps: [...workflow.steps, newStep],
        });
      } else {
        // Create new workflow
        await api.post('/workflows', {
          task_type_id: taskTypeId,
          steps: [newStep],
        });
      }

      setStepForm({ title: '', description: '', screenshot_url: '' });
      setShowAddForm(null);
      fetchData();
    } catch (error) {
      alert('Failed to add step');
    }
  };

  const handleUpdateStep = async (workflowId, stepId, updatedStep) => {
    try {
      const workflow = workflows.find(w => w.id === workflowId);
      const updatedSteps = workflow.steps.map(s => 
        s.id === stepId ? { ...s, ...updatedStep } : s
      );
      
      await api.put(`/workflows/${workflowId}`, { steps: updatedSteps });
      setEditingWorkflow(null);
      fetchData();
    } catch (error) {
      alert('Failed to update step');
    }
  };

  const handleDeleteStep = async (workflowId, stepId) => {
    if (!window.confirm('Are you sure you want to delete this step?')) return;
    
    try {
      const workflow = workflows.find(w => w.id === workflowId);
      const updatedSteps = workflow.steps.filter(s => s.id !== stepId);
      
      await api.put(`/workflows/${workflowId}`, { steps: updatedSteps });
      fetchData();
    } catch (error) {
      alert('Failed to delete step');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Workflows</h2>
          <p className="text-gray-600">Step-by-step guides for sub-services</p>
        </div>
      </div>

      {/* Services and Task Types */}
      {services.map(service => {
        const serviceTaskTypes = taskTypes.filter(tt => tt.service_id === service.id);
        if (serviceTaskTypes.length === 0) return null;

        return (
          <div key={service.id} className="bg-white rounded-lg shadow border">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="font-semibold text-lg">{service.name}</h3>
            </div>
            <div className="divide-y">
              {serviceTaskTypes.map(taskType => {
                const workflow = getWorkflowForTaskType(taskType.id);
                const isExpanded = expandedTaskType === taskType.id;

                return (
                  <div key={taskType.id}>
                    {/* Task Type Header */}
                    <div
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                      onClick={() => setExpandedTaskType(isExpanded ? null : taskType.id)}
                    >
                      <div className="flex items-center">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 mr-2 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 mr-2 text-gray-500" />
                        )}
                        <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
                        <span className="font-medium">{taskType.name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">
                          {workflow?.steps?.length || 0} steps
                        </span>
                        {canEdit && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowAddForm(taskType.id);
                              setExpandedTaskType(taskType.id);
                            }}
                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            <Plus className="w-4 h-4 inline mr-1" />
                            Add Step
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Workflow Steps */}
                    {isExpanded && (
                      <div className="px-4 pb-4 bg-gray-50">
                        {/* Add Step Form */}
                        {showAddForm === taskType.id && canEdit && (
                          <div className="mb-4 p-4 bg-white rounded-lg border">
                            <h4 className="font-medium mb-3">Add New Step</h4>
                            <div className="space-y-3">
                              <input
                                type="text"
                                placeholder="Step Title *"
                                value={stepForm.title}
                                onChange={(e) => setStepForm({ ...stepForm, title: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg"
                              />
                              <textarea
                                placeholder="Description / Instructions"
                                value={stepForm.description}
                                onChange={(e) => setStepForm({ ...stepForm, description: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg"
                                rows={3}
                              />
                              <input
                                type="url"
                                placeholder="Screenshot URL (optional)"
                                value={stepForm.screenshot_url}
                                onChange={(e) => setStepForm({ ...stepForm, screenshot_url: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg"
                              />
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleAddStep(taskType.id)}
                                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                >
                                  <Save className="w-4 h-4 inline mr-1" />
                                  Save Step
                                </button>
                                <button
                                  onClick={() => {
                                    setShowAddForm(null);
                                    setStepForm({ title: '', description: '', screenshot_url: '' });
                                  }}
                                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Steps List */}
                        {workflow?.steps?.length > 0 ? (
                          <div className="space-y-3">
                            {workflow.steps.map((step, index) => (
                              <div key={step.id} className="bg-white p-4 rounded-lg border">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center mb-2">
                                      <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold mr-3">
                                        {index + 1}
                                      </span>
                                      <h5 className="font-semibold">{step.title}</h5>
                                    </div>
                                    {step.description && (
                                      <p className="text-gray-600 ml-11 mb-2">{step.description}</p>
                                    )}
                                    {step.screenshot_url && (
                                      <div className="ml-11">
                                        <img
                                          src={step.screenshot_url}
                                          alt={step.title}
                                          className="max-w-md rounded-lg border"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                  {canEdit && (
                                    <div className="flex space-x-2 ml-4">
                                      <button
                                        onClick={() => setEditingWorkflow({ workflowId: workflow.id, stepId: step.id, ...step })}
                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteStep(workflow.id, step.id)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                            <p>No workflow steps defined yet.</p>
                            {canEdit && (
                              <p className="text-sm">Click "Add Step" to create a workflow guide.</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Edit Step Modal */}
      {editingWorkflow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Edit Step</h3>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Step Title *"
                value={editingWorkflow.title}
                onChange={(e) => setEditingWorkflow({ ...editingWorkflow, title: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <textarea
                placeholder="Description / Instructions"
                value={editingWorkflow.description || ''}
                onChange={(e) => setEditingWorkflow({ ...editingWorkflow, description: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
              />
              <input
                type="url"
                placeholder="Screenshot URL (optional)"
                value={editingWorkflow.screenshot_url || ''}
                onChange={(e) => setEditingWorkflow({ ...editingWorkflow, screenshot_url: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <div className="flex space-x-2">
                <button
                  onClick={() => handleUpdateStep(
                    editingWorkflow.workflowId,
                    editingWorkflow.stepId,
                    { title: editingWorkflow.title, description: editingWorkflow.description, screenshot_url: editingWorkflow.screenshot_url }
                  )}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setEditingWorkflow(null)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Workflows;
